import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../context/ApiContext';

function Card({ title, value, subtitle }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
      {subtitle ? <div className="text-xs text-gray-500 mt-1">{subtitle}</div> : null}
    </div>
  );
}

function SimpleBarChart({ data = [], height = 160, showAxis = true, color = '#fb923c', rotateLabels = false, labelMax = 10 }) {
  const max = Math.max(1, ...data.map(d => d.value || 0));
  const barW = 24;
  const gap = 8;
  const padLeft = showAxis ? 36 : 8;
  const padBottom = 28;
  const padTop = 8;
  const padRight = 8;
  const innerH = height - padTop - padBottom;
  const width = padLeft + data.length * (barW + gap) + gap + padRight;
  const yTicks = showAxis ? [0, Math.round(max / 2), max] : [];
  const labelStep = Math.max(1, Math.ceil(data.length / 12));
  const fmtLabel = (s) => {
    const str = String(s || '');
    return str.length > labelMax ? str.slice(0, labelMax) + '…' : str;
  };

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="bar chart">
      {showAxis && (
        <>
          {/* X axis */}
          <line x1={padLeft} y1={padTop + innerH} x2={width - padRight} y2={padTop + innerH} stroke="#e5e7eb" />
          {/* Y axis and ticks */}
          {yTicks.map((t, i) => {
            const y = padTop + innerH - Math.round((t / max) * innerH);
            return (
              <g key={i}>
                <line x1={padLeft - 4} y1={y} x2={width - padRight} y2={y} stroke="#f3f4f6" />
                <text x={padLeft - 8} y={y + 3} fontSize="10" fill="#6b7280" textAnchor="end">{t}</text>
              </g>
            );
          })}
        </>
      )}
      {data.map((d, i) => {
        const val = d.value || 0;
        const h = Math.round((val / max) * innerH);
        const x = padLeft + gap + i * (barW + gap);
        const y = padTop + (innerH - h);
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} fill={color} rx="4">
              <title>{`${d.label}: ${val}`}</title>
            </rect>
            {i % labelStep === 0 && (
              rotateLabels ? (
                <text
                  x={x + barW / 2}
                  y={padTop + innerH + 18}
                  fontSize="10"
                  fill="#6b7280"
                  textAnchor="end"
                  transform={`rotate(-45 ${x + barW / 2},${padTop + innerH + 18})`}
                >
                  {fmtLabel(d.label)}
                </text>
              ) : (
                <text x={x + barW / 2} y={padTop + innerH + 14} fontSize="10" textAnchor="middle" fill="#6b7280">{fmtLabel(d.label)}</text>
              )
            )}
          </g>
        );
      })}
    </svg>
  );
}

function SimpleLineChart({ points = [], height = 140, color = '#2563eb', showDots = false }) {
  const max = Math.max(1, ...points);
  const w = 320;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const coords = points.map((v, i) => {
    const x = i * step;
    const y = height - Math.round((v / max) * (height - 16)) - 8;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={height} role="img" aria-label="line chart">
      <polyline fill="none" stroke={color} strokeWidth="2" points={coords} />
      {showDots && points.map((v, i) => {
        const x = i * step;
        const y = height - Math.round((v / max) * (height - 16)) - 8;
        return <circle key={i} cx={x} cy={y} r={3} fill={color}><title>{v}</title></circle>;
      })}
    </svg>
  );
}

function SimpleMultiLineChart({ series = [], height = 140 }) {
  const w = 360;
  const max = Math.max(1, ...series.flatMap(s => s.points));
  const count = Math.max(1, ...series.map(s => s.points.length));
  const step = count > 1 ? w / (count - 1) : w;
  const colors = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b'];
  return (
    <svg width={w} height={height} role="img" aria-label="multi line chart">
      {series.map((s, idx) => {
        const coords = s.points.map((v, i) => {
          const x = i * step;
          const y = height - Math.round((v / max) * (height - 16)) - 8;
          return `${x},${y}`;
        }).join(' ');
        const color = s.color || colors[idx % colors.length];
        return <polyline key={idx} fill="none" stroke={color} strokeWidth="2" points={coords} />;
      })}
    </svg>
  );
}

export default function AnalyticsDashboard() {
  const { adminListApplicants, adminListTenants, fetchProperties, fetchUnitsForMany } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apps, setApps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [inactives, setInactives] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propFilter, setPropFilter] = useState('');
  // Default date window: last 14 days
  const defaultEnd = new Date();
  const defaultStart = new Date(); defaultStart.setDate(defaultEnd.getDate() - 13);
  const [startDate, setStartDate] = useState(defaultStart.toISOString().slice(0,10));
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().slice(0,10));

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError('');
        // load applicants (page through a couple of pages to avoid heavy loads)
        let cursor = undefined; const allApps = [];
        for (let i = 0; i < 4; i++) {
          const res = await adminListApplicants({ limit: 100, cursor });
          const items = Array.isArray(res) ? res : (res?.items || []);
          allApps.push(...items);
          cursor = Array.isArray(res) ? null : res?.nextCursor;
          if (!cursor) break;
        }
        setApps(allApps);

        // load tenants (active)
        cursor = undefined; const allTen = [];
        for (let i = 0; i < 4; i++) {
          const res = await adminListTenants({ status: 'active', limit: 100, cursor });
          const items = Array.isArray(res) ? res : (res?.items || []);
          allTen.push(...items);
          cursor = Array.isArray(res) ? null : res?.nextCursor;
          if (!cursor) break;
        }
        setTenants(allTen);

        // load tenants (inactive) for occupancy outflow
        cursor = undefined; const allInactives = [];
        for (let i = 0; i < 4; i++) {
          const res = await adminListTenants({ status: 'inactive', limit: 100, cursor });
          const items = Array.isArray(res) ? res : (res?.items || []);
          allInactives.push(...items);
          cursor = Array.isArray(res) ? null : res?.nextCursor;
          if (!cursor) break;
        }
        setInactives(allInactives);

        // load units across properties
        const props = await fetchProperties();
        setProperties(Array.isArray(props) ? props : []);
        const slugs = (props || []).map(p => p.slug).filter(Boolean);
        const map = await fetchUnitsForMany(slugs);
        const flattened = Object.entries(map || {}).flatMap(([slug, arr]) => (Array.isArray(arr) ? arr : []).map(u => ({ ...u, __slug: slug })));
        setUnits(flattened);

      } catch (e) {
        setError(e?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, [adminListApplicants, adminListTenants, fetchProperties, fetchUnitsForMany]);

  // Helpers
  const inDateRange = (iso) => {
    if (!iso) return false;
    const d = String(iso).slice(0,10);
    return (!startDate || d >= startDate) && (!endDate || d <= endDate);
  };

  const filteredApps = useMemo(() => {
    return (apps || []).filter(a => (!propFilter || a.propertySlug === propFilter) && inDateRange(a.createdAt || a.updatedAt));
  }, [apps, propFilter, startDate, endDate]);

  const filteredTenants = useMemo(() => {
    return (tenants || []).filter(t => (!propFilter || t.propertySlug === propFilter) && inDateRange(t.updatedAt || t.createdAt));
  }, [tenants, propFilter, startDate, endDate]);

  const filteredUnits = useMemo(() => {
    return (units || []).filter(u => (!propFilter || u.__slug === propFilter));
  }, [units, propFilter]);

  // KPIs (respect filters)
  const kpis = useMemo(() => {
    const totalApps = filteredApps.length;
    const approved = filteredApps.filter(a => String(a.status || '').toLowerCase().includes('approved')).length;
    const totalTenants = filteredTenants.length;
    const totalUnits = filteredUnits.length;
    const occupiedUnits = filteredUnits.filter(u => u.occupantTenantId && !u.available).length;
    const occupancy = totalUnits ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
    return { totalApps, approved, totalTenants, totalUnits, occupiedUnits, occupancy };
  }, [filteredApps, filteredTenants, filteredUnits]);

  // Applications by day (last 14 days)
  const appsByDay = useMemo(() => {
    // Build buckets for selected date range (cap 31 days for readability)
    const start = startDate ? new Date(startDate) : new Date();
    const end = endDate ? new Date(endDate) : new Date();
    const ms = Math.max(0, Math.min(31, Math.ceil((end - start) / (24*3600*1000)) + 1));
    const days = [];
    for (let i = 0; i < ms; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      const key = d.toISOString().slice(0,10);
      const value = filteredApps.filter(a => String(a.createdAt || a.updatedAt || '').slice(0,10) === key).length;
      days.push({ label: key.slice(5), value });
    }
    return days;
  }, [filteredApps, startDate, endDate]);

  // Tenants by property (top 5)
  const tenantsByProperty = useMemo(() => {
    const map = new Map();
    (filteredTenants || []).forEach(t => {
      map.set(t.propertySlug, (map.get(t.propertySlug) || 0) + 1);
    });
    const arr = Array.from(map.entries()).map(([label, value]) => ({ label: label || '-', value }));
    arr.sort((a,b) => b.value - a.value);
    return arr.slice(0, 5);
  }, [filteredTenants]);

  // Saved views
  const [views, setViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rf.analytics.views') || '[]'); } catch { return []; }
  });
  const [selectedView, setSelectedView] = useState('');

  function saveCurrentView() {
    const name = window.prompt('Save view as:', 'My View');
    if (!name) return;
    const v = { id: crypto.randomUUID(), name, propFilter, startDate, endDate };
    const next = [...views, v];
    setViews(next);
    localStorage.setItem('rf.analytics.views', JSON.stringify(next));
    setSelectedView(v.id);
  }
  function applyView(id) {
    const v = views.find(x => x.id === id);
    if (!v) return;
    setPropFilter(v.propFilter || '');
    setStartDate(v.startDate || startDate);
    setEndDate(v.endDate || endDate);
    setSelectedView(id);
  }
  function deleteView(id) {
    const next = views.filter(v => v.id !== id);
    setViews(next);
    localStorage.setItem('rf.analytics.views', JSON.stringify(next));
    if (selectedView === id) setSelectedView('');
  }

  // Export CSV for KPIs and charts
  function exportCsv() {
    const lines = [];
    lines.push('Section,Metric,Value');
    lines.push(`KPIs,Applicants,${kpis.totalApps}`);
    lines.push(`KPIs,Approved,${kpis.approved}`);
    lines.push(`KPIs,Active Tenants,${kpis.totalTenants}`);
    lines.push(`KPIs,Occupied Units,${kpis.occupiedUnits}`);
    lines.push(`KPIs,Total Units,${kpis.totalUnits}`);
    lines.push(`KPIs,Occupancy %,${kpis.occupancy}`);
    lines.push('');
    lines.push('Applications By Day,Date,Count');
    appsByDay.forEach(d => lines.push(`Applications By Day,${d.label},${d.value}`));
    lines.push('');
    lines.push('Tenants By Property,Property,Count');
    tenantsByProperty.forEach(d => lines.push(`Tenants By Property,${d.label},${d.value}`));

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'analytics.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  // Multi-series: Applicants vs Approved over time
  const approvalsByDay = useMemo(() => {
    const start = startDate ? new Date(startDate) : new Date();
    const end = endDate ? new Date(endDate) : new Date();
    const ms = Math.max(0, Math.min(31, Math.ceil((end - start) / (24*3600*1000)) + 1));
    const pointsA = [];
    const pointsB = [];
    for (let i = 0; i < ms; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      const key = d.toISOString().slice(0,10);
      const appsCount = filteredApps.filter(a => String(a.createdAt || a.updatedAt || '').slice(0,10) === key).length;
      const approvedCount = filteredApps.filter(a => String(a.status || '').toLowerCase().includes('approved') && String(a.updatedAt || a.createdAt || '').slice(0,10) === key).length;
      pointsA.push(appsCount);
      pointsB.push(approvedCount);
    }
    return { pointsA, pointsB };
  }, [filteredApps, startDate, endDate]);

  // Occupancy trend (approx): cumulative approvals minus cumulative inactives, divided by unit count
  const occupancyTrend = useMemo(() => {
    const start = startDate ? new Date(startDate) : new Date();
    const end = endDate ? new Date(endDate) : new Date();
    const ms = Math.max(0, Math.min(31, Math.ceil((end - start) / (24*3600*1000)) + 1));
    const totalUnits = filteredUnits.length || units.length || 1;
    let cumIn = 0; let cumOut = 0;
    const points = [];
    for (let i = 0; i < ms; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      const key = d.toISOString().slice(0,10);
      const inToday = filteredApps.filter(a => String(a.status || '').toLowerCase().includes('approved') && String(a.updatedAt || a.createdAt || '').slice(0,10) === key).length;
      const outToday = (inactives || []).filter(t => (!propFilter || t.propertySlug === propFilter) && String(t.updatedAt || t.createdAt || '').slice(0,10) === key).length;
      cumIn += inToday; cumOut += outToday;
      const occCount = Math.max(0, cumIn - cumOut);
      const percent = Math.round((occCount / totalUnits) * 100);
      points.push(percent);
    }
    return points;
  }, [filteredApps, inactives, filteredUnits, units, propFilter, startDate, endDate]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Analytics</h1>
      </div>

      {error && (
        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-600">Loading…</div>
      ) : (
        <>
          {/* Filters */}
          <div className="rounded-xl border bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">Property</div>
                <select className="border rounded px-3 py-2" value={propFilter} onChange={(e)=>setPropFilter(e.target.value)}>
                  <option value="">All</option>
                  {properties.map(p => (
                    <option key={p.slug} value={p.slug}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Start Date</div>
                <input type="date" className="border rounded px-3 py-2" value={startDate} onChange={(e)=>setStartDate(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">End Date</div>
                <input type="date" className="border rounded px-3 py-2" value={endDate} onChange={(e)=>setEndDate(e.target.value)} />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <select className="border rounded px-3 py-2" value={selectedView} onChange={(e)=>applyView(e.target.value)}>
                  <option value="">Saved Views</option>
                  {views.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <button className="px-3 py-2 border rounded" onClick={saveCurrentView}>Save View</button>
                {selectedView && <button className="px-3 py-2 border rounded" onClick={()=>deleteView(selectedView)}>Delete View</button>}
                <button className="px-3 py-2 border rounded" onClick={exportCsv}>Export CSV</button>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Showing data {propFilter ? `for ${properties.find(p=>p.slug===propFilter)?.name || propFilter}` : 'for all properties'} between {startDate} and {endDate}.
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card title="Applicants" value={kpis.totalApps} subtitle={`${kpis.approved} approved`} />
            <Card title="Active Tenants" value={kpis.totalTenants} />
            <Card title="Occupancy" value={`${kpis.occupancy}%`} subtitle={`${kpis.occupiedUnits}/${kpis.totalUnits} units`} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Applications over time</div>
              <SimpleBarChart data={appsByDay} />
            </div>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Tenants by Property (Top 5)</div>
              <SimpleBarChart data={tenantsByProperty} rotateLabels labelMax={14} />
            </div>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Applicants vs Approved (trend)</div>
              <div className="flex items-center gap-4 mb-2 text-xs">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-full" style={{background:'#2563eb'}} /> Applicants</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block rounded-full" style={{background:'#dc2626'}} /> Approved</span>
              </div>
              <SimpleMultiLineChart series={[{ points: approvalsByDay.pointsA, color:'#2563eb' }, { points: approvalsByDay.pointsB, color:'#dc2626' }]} />
            </div>
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Occupancy Trend (approx)</div>
              <SimpleLineChart points={occupancyTrend} color="#16a34a" showDots />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
